import {
  DOCUMENT_SECTIONS,
  DocumentSection,
  DossierTemplateKind,
  EVIDENCE_IN_PROGRESS_STATUSES,
  EvidenceAttachmentRef,
  EvidenceAttentionCode,
  EvidenceAttentionItem,
  EvidenceDraftSummary,
  EvidenceSlot,
  ExcludableSection,
  ForceableSection,
  OutlineRefund,
  OutlineSection,
  SECTION_SLOTS,
} from "../../types/evidence";

export interface SectionRowSpec {
  heading: string;
  /** The status line when nothing is open; none on a row whose body says it already. */
  source?: string;
  /** The status line a digital draft's row states instead. */
  digitalSource?: string;
  slots: readonly EvidenceSlot[];
  /** Attention codes whose message becomes the row's status line (no slot of their own). */
  notes?: EvidenceAttentionCode[];
}

export const SECTION_ROWS: Record<DocumentSection, SectionRowSpec> = {
  productDescription: { heading: "Product Description", slots: SECTION_SLOTS.productDescription },
  factsOfTheCase: { heading: "Facts of the Case", slots: SECTION_SLOTS.factsOfTheCase },
  orderHistory: { heading: "Order History", source: "The customer's earlier orders", slots: SECTION_SLOTS.orderHistory },
  customerCommunication: { heading: "Customer Communication", source: "Your screenshots, in this order", slots: SECTION_SLOTS.customerCommunication },
  customerInformation: { heading: "Customer Information", source: "Name, address and email from the order", slots: SECTION_SLOTS.customerInformation },
  securedPayment: { heading: "Secured Payment", source: "CVC and AVS results from the order", slots: SECTION_SLOTS.securedPayment },
  orderReceipt: {
    heading: "Order Receipt and Confirmation",
    source: "Receipt from the order. Add the confirmation email if you have it.",
    slots: SECTION_SLOTS.orderReceipt,
  },
  proofOfRefund: { heading: "Proof of Refund", slots: SECTION_SLOTS.proofOfRefund },
  ipLocation: {
    heading: "IP Location",
    source: "Checkout IP against the billing address",
    slots: SECTION_SLOTS.ipLocation,
    notes: ["ip_location_mismatch", "ip_location_unavailable", "ip_location_same_state"],
  },
  fulfillment: {
    heading: "Proof of Fulfillment",
    source: "Carrier tracking. Add the carrier's delivery photo if there is one.",
    slots: SECTION_SLOTS.fulfillment,
  },
  digitalUsage: {
    heading: "Proof of Digital Delivery and Usage",
    source: "Your screenshots of the account log, the product use and the email log",
    slots: SECTION_SLOTS.digitalUsage,
  },
  checkoutProcess: { heading: "Checkout Process", source: "Product page and checkout screenshots", slots: SECTION_SLOTS.checkoutProcess },
  policies: {
    heading: "Policies",
    source: "Cancellation, refund and shipping, from storefront settings",
    digitalSource: "Cancellation and refund, from storefront settings",
    slots: SECTION_SLOTS.policies,
  },
};

export function sectionRow(key: DocumentSection, template: DossierTemplateKind): SectionRowSpec {
  const spec = SECTION_ROWS[key];
  return template === "digital" && spec.digitalSource ? { ...spec, source: spec.digitalSource } : spec;
}

/** Slots always offered on their row even when no attention item names them. The digital ones sit on
 * a row the API lists only for a digital draft. */
export const ALWAYS_OFFERED_SLOTS: readonly EvidenceSlot[] = [
  "proof_of_delivery", "tracking_page", "account_activity", "product_usage", "email_activity",
];

export function rowOfSlot(slot: EvidenceSlot): DocumentSection | null {
  return DOCUMENT_SECTIONS.find((key) => SECTION_ROWS[key].slots.includes(slot)) ?? null;
}

export function itemsForRow(key: DocumentSection, items: EvidenceAttentionItem[]): EvidenceAttentionItem[] {
  const spec = SECTION_ROWS[key];
  return items.filter((item) =>
    (item.attachableSlot && spec.slots.includes(item.attachableSlot)) || spec.notes?.includes(item.code));
}

/**
 * What the agent can still act on. The items are as of the last generation, so a slot filled since
 * and a support desk attested since close theirs here; a generated exhibit already prints.
 */
export function openItems(items: EvidenceAttentionItem[], files: EvidenceAttachmentRef[], noContactAttested: boolean): EvidenceAttentionItem[] {
  return items.filter((item) => {
    if (item.generated) return false;
    if (item.code === "customer_comms_missing" && noContactAttested) return false;
    // Named on the account log, but the product-use screenshot answers it as well.
    if (item.code === "digital_proof_missing" && files.some((f) => f.slot === "product_usage")) return false;
    return !item.attachableSlot || !files.some((f) => f.slot === item.attachableSlot);
  });
}

/** Files whose section is not in this document, so they would print unseen. */
export function unlistedFiles(sections: OutlineSection[], files: EvidenceAttachmentRef[]): EvidenceAttachmentRef[] {
  return files.filter((f) => {
    const key = rowOfSlot(f.slot);
    return !key || !sections.some((s) => s.key === key);
  });
}

/**
 * A row still asking for a screenshot starts on, so the ask is seen. The run's own verdict on the row
 * (an IP that did not match or could not be looked up) keeps it off with its note, unless the agent
 * ticked it in. A file in one of the row's slots keeps it on until the next outline says it prints.
 */
export function rowIsOn(
  row: OutlineSection,
  excluded: ExcludableSection[],
  included: ForceableSection[],
  revealed: Set<DocumentSection>,
  openItems: EvidenceAttentionItem[],
  files: EvidenceAttachmentRef[],
  /** A contact answer has been given, and any answer prints Customer Communication by itself. */
  answerPrints = false,
): boolean {
  const spec = SECTION_ROWS[row.key];
  const notes = spec.notes ?? [];
  const hasAsk = !openItems.some((item) => notes.includes(item.code)) && openItems.some((item) => item.attachableSlot && !item.generated);
  const filled = files.some((f) => spec.slots.includes(f.slot));
  // Such an answer prints the section on the next run, so the row is on as soon as it is given.
  const answered = row.key === "customerCommunication" && answerPrints;
  return !excluded.includes(row.key as ExcludableSection)
    // A tick-in outlives the regenerate that took away what it printed; it counts only while the row can be forced.
    && (row.defaultOn || answered || (row.forceable && included.includes(row.key as ForceableSection)) || revealed.has(row.key) || hasAsk || filled);
}

/** Nobody has said whether the customer contacted support. */
export function contactUnanswered(draft: EvidenceDraftSummary): boolean {
  return !draft.no_contact_attested && !draft.input_overrides?.customerContact;
}

/** The conclusion's ask: a write that failed. */
export function conclusionAsk(draft: EvidenceDraftSummary): EvidenceAttentionItem["severity"] | null {
  if (EVIDENCE_IN_PROGRESS_STATUSES.includes(draft.status)) return null;
  if (!draft.conclusion) return draft.generated_at ? "required" : "recommended";
  return null;
}

export function needsInput(
  row: OutlineSection, on: boolean, openItems: EvidenceAttentionItem[], refund: OutlineRefund, files: EvidenceAttachmentRef[], contactOpen: boolean,
): boolean {
  // The contact question sits on this row and is open whether or not the row prints yet.
  if (row.key === "customerCommunication" && contactOpen) return true;
  if (!on) return false;
  // A note only informs; what counts is a slot the agent can still fill.
  if (openItems.some((item) => item.attachableSlot)) return true;
  return row.key === "proofOfRefund" && refund.source !== "store" && !files.some((f) => f.slot === "proof_of_refund");
}
